import React, { useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Button,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddHomeIcon from "@mui/icons-material/AddHome";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import { getUser } from "../Utils/Common";
import { RoleBasedRender } from "../components/RoleBaseRender";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

function HomePage() {
  const theme = useTheme();
  const [user, setUser] = React.useState("");
  const history = useHistory();

  useEffect(async () => {
    let userData = getUser();
    console.log(userData);
    setUser(userData.fname + " " + userData.lname);
  });

  const QuickAccessCard = ({
    icon,
    title,
    description,
    buttonText,
    onClick,
    color,
  }) => (
    <Card
      elevation={1}
      sx={{
        borderRadius: 2,
        height: "100%",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 3,
        },
      }}
    >
      <CardContent
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              display: "flex",
              mr: 2,
              bgcolor: alpha(color || theme.palette.primary.main, 0.1),
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" fontWeight="600" color="text.primary">
            {title}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, flexGrow: 1 }}
        >
          {description}
        </Typography>

        <Button
          variant="text"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={onClick}
          sx={{ alignSelf: "flex-start" }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <HomeIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
        <Box>
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Welcome to Nortel
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Factory Management System
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h6"
          fontWeight="600"
          color="text.primary"
          sx={{ mb: 3 }}
        >
          Quick Access
        </Typography>

        <Grid container spacing={3}>
          <RoleBasedRender
            user={user}
            allowedRoles={[
              "ROLE_ADMIN",
              "INVENTORY_VIEW",
              "INVENTORY_UPDATE",
              "INVENTORY_CREATE",
              "INVENTORY_DELETE",
            ]}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickAccessCard
                icon={<InventoryIcon color="primary" />}
                title="Inventory"
                description="Manage your inventory items, track stock levels, and handle warehouse transfers."
                buttonText="View Inventory"
                onClick={() => {
                  history.push("/template/im_items");
                }}
                color={theme.palette.primary.main}
              />
            </Grid>
          </RoleBasedRender>
          <RoleBasedRender
            user={user}
            allowedRoles={[
              "ROLE_ADMIN",
              "SALE_PRODUCT_VIEW",
              "SALE_PRODUCT_CREATE",
              "SALE_PRODUCT_UPDATE",
              "SALE_PRODUCT_DELETE",
            ]}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickAccessCard
                icon={
                  <ShoppingCartIcon
                    sx={{ color: theme.palette.success.main }}
                  />
                }
                title="Sales"
                description="Create and manage sales orders, invoices, and track revenue performance."
                buttonText="View Sales"
                onClick={() => {
                  history.push("/template/sale_products");
                }}
                color={theme.palette.success.main}
              />
            </Grid>
          </RoleBasedRender>
          <RoleBasedRender
            user={user}
            allowedRoles={[
              "ROLE_ADMIN",
              "WT_BILL_VIEW",
              "WT_BILL_CREATE",
              "WT_BILL_APPROVE",
              "WT_BILL_DELETE",
            ]}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickAccessCard
                icon={
                  <StorefrontIcon sx={{ color: theme.palette.warning.main }} />
                }
                title="Retail"
                description="Manage retail operations, customer interactions, and retail billing."
                buttonText="View Retail"
                onClick={() => {
                  history.push("/template/sale_products");
                }}
                color={theme.palette.warning.main}
              />
            </Grid>
          </RoleBasedRender>
          <RoleBasedRender
            user={user}
            allowedRoles={[
              "ROLE_ADMIN",
              "CUSTOMER_VIEW",
              "CUSTOMER_CREATE",
              "CUSTOMER_UPDATE",
              "CUSTOMER_DISABLE",
            ]}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <QuickAccessCard
                icon={<PeopleIcon sx={{ color: theme.palette.info.main }} />}
                title="Customers"
                description="View and manage customer information, orders, and relationships."
                buttonText="View Customers"
                onClick={() => {
                  history.push("/template/customers");
                }}
                color={theme.palette.info.main}
              />
            </Grid>
          </RoleBasedRender>
        </Grid>
      </Box>

      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h6"
          fontWeight="600"
          color="text.primary"
          sx={{ mb: 3 }}
        >
          System Features
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="primary.main"
                  sx={{ mb: 2 }}
                >
                  Factory Management
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <RoleBasedRender
                    user={user}
                    allowedRoles={[
                      "ROLE_ADMIN",
                      "WAREHOUSE_CREATE",
                      "WAREHOUSE_VIEW",
                      "WAREHOUSE_UPDATE",
                      "WAREHOUSE_DELETE",
                    ]}
                  >
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => {
                          history.push("/template/warehouses");
                        }}
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            cursor: "pointer",
                          },
                        }}
                      >
                        <WarehouseIcon color="primary" />
                        <Typography fontWeight="500">
                          Warehouse Management
                        </Typography>
                      </Box>
                    </Grid>
                  </RoleBasedRender>
                  <RoleBasedRender
                    user={user}
                    allowedRoles={[
                      "ROLE_ADMIN",
                      "IMPORT_STOCK_VIEW",
                      "IMPORT_STOCK_UPDATE",
                      "IMPORT_STOCK_CREATE",
                      "IMPORT_STOCK_DELETE",
                    ]}
                  >
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => {
                          history.push("/template/im_stockupdates");
                        }}
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            cursor: "pointer",
                          },
                        }}
                      >
                        <AddHomeIcon color="primary" />
                        <Typography fontWeight="500">
                          Import Stock Updates
                        </Typography>
                      </Box>
                    </Grid>
                  </RoleBasedRender>
                  <RoleBasedRender
                    user={user}
                    allowedRoles={[
                      "ROLE_ADMIN",
                      "SALE_BILL_VIEW",
                      "SALE_BILL_CREATE",
                      "SALE_BILL_APPROVE",
                      "SALE_BILL_DELETE",
                    ]}
                  >
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => {
                          history.push("/template/sale_bills");
                        }}
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            cursor: "pointer",
                          },
                        }}
                      >
                        <ReceiptIcon color="primary" />
                        <Typography fontWeight="500">
                          Sale Billing & Invoices
                        </Typography>
                      </Box>
                    </Grid>
                  </RoleBasedRender>
                  <RoleBasedRender
                    user={user}
                    allowedRoles={[
                      "ROLE_ADMIN",
                      "PRODUCT_VIEW",
                      "PRODUCT_UPDATE",
                      "PRODUCT_CREATE",
                      "PRODUCT_DELETE",
                    ]}
                  >
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => {
                          history.push("/template/ex_items");
                        }}
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            cursor: "pointer",
                          },
                        }}
                      >
                        <DashboardCustomizeIcon color="primary" />
                        <Typography fontWeight="500">
                          Production Planning
                        </Typography>
                      </Box>
                    </Grid>
                  </RoleBasedRender>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <RoleBasedRender user={user} allowedRoles={["ROLE_ADMIN"]}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 2,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="primary.main"
                    sx={{ mb: 2 }}
                  >
                    System Administration
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => {
                          history.push("/template/users");
                        }}
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            cursor: "pointer",
                          },
                        }}
                      >
                        <PersonIcon color="primary" />
                        <Typography fontWeight="500">
                          User Management
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            cursor: "pointer",
                          },
                        }}
                      >
                        <SettingsIcon color="primary" />
                        <Typography fontWeight="500">
                          System Settings
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </RoleBasedRender>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Card
          elevation={1}
          sx={{
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="primary.main"
                  gutterBottom
                >
                  Explore the Nortel Factory Management System
                </Typography>
                <Typography variant="body1" paragraph>
                  Streamline your factory operations with our comprehensive
                  management tools. Manage inventory, track sales, handle retail
                  operations, and more.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    boxShadow: 2,
                    "&:hover": {
                      boxShadow: 4,
                    },
                  }}
                >
                  Get Started
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <img
                    src="/api/placeholder/300/200"
                    alt="Factory Management"
                    style={{ maxWidth: "100%", borderRadius: 8 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Paper>
  );
}

export default HomePage;
